import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, FileDown, ExternalLink, Check, Palette } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE, getToken } from "@/lib/api";
import { SECTIONS, mergeData } from "@/data/organizerSchema";
import AuthModal from "@/components/AuthModal";
import AIAssistant from "@/components/AIAssistant";
import OrganizerSales from "@/components/OrganizerSales";
import LogoUpload from "@/components/LogoUpload";
import IdeaSwitcher from "@/components/IdeaSwitcher";
import RichField, { isRichEmpty, richToText } from "@/components/editor/RichField";

function Swatches({ value }) {
  const hexes = (richToText(value).match(/#(?:[0-9a-fA-F]{3}){1,2}\b/g) || []).slice(0, 6);
  if (!hexes.length) return null;
  return (
    <div className="flex gap-2 mt-2">
      {hexes.map((h) => (
        <span key={h} className="flex items-center gap-1.5">
          <span className="w-6 h-6 rounded-md border border-[#3A2E25]/15" style={{ background: h }} />
          <span className="f-mono text-[10px] text-[#8C7562]">{h}</span>
        </span>
      ))}
    </div>
  );
}

export default function OrganizerPage() {
  const { user, ready } = useAuth();
  const [data, setData] = useState(null);
  const [logo, setLogo] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [ideaId, setIdeaId] = useState(null);
  const [aiKey, setAiKey] = useState(0);
  const [active, setActive] = useState("identity");
  const [saveState, setSaveState] = useState("saved");
  const [aiOpen, setAiOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [kitBusy, setKitBusy] = useState(false);
  const timer = useRef(null);

  const downloadFile = async (path, filename) => {
    await api.put("/organizer", { data }, { params: ideaParams() });
    const res = await fetch(`${API_BASE}${path}${ideaId ? `?idea=${ideaId}` : ""}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error();
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadBrandKit = async () => {
    setKitBusy(true);
    try {
      await downloadFile("/organizer/brand-kit", "brand-kit.pdf");
      toast.success("Brand kit downloaded — one page with your logo, palette and fonts.");
    } catch {
      toast.error("Couldn't build the brand kit — try again.");
    } finally {
      setKitBusy(false);
    }
  };

  const loadIdea = (id) => {
    setData(null);
    api.get("/organizer", { params: id ? { idea: id } : {} }).then(({ data: d }) => {
      setIdeaId(d.idea.idea_id);
      setData(mergeData(d.data));
      setLogo(d.logo || null);
      setAiOpen(false);
      setAiKey((k) => k + 1);
    }).catch(() => setData(mergeData()));
  };

  useEffect(() => {
    if (!user?.paid) return;
    api.get("/organizer/ideas").then(({ data: d }) => setIdeas(d.ideas)).catch(() => {});
    loadIdea(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const ideaParams = () => (ideaId ? { idea: ideaId } : {});

  const update = (section, key, value) => {
    setData((prev) => {
      const next = { ...prev, [section]: { ...prev[section], [key]: value } };
      setSaveState("saving");
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        api.put("/organizer", { data: next }, { params: ideaParams() })
          .then(({ data: r }) => { setSaveState("saved"); if (r.name) setIdeas((list) => list.map((i) => (i.idea_id === ideaId ? { ...i, name: r.name } : i))); })
          .catch(() => { setSaveState("saved"); toast.error("Couldn't save — check your connection."); });
      }, 700);
      return next;
    });
  };

  const applyFromCopilot = ({ data: merged, applied }) => {
    setData(mergeData(merged));
    setActive(applied[0].section);
    toast.success(`Applied to ${applied.map((a) => a.label).join(", ")}`);
  };

  const filledCount = (sec) => sec.fields.filter((f) => !isRichEmpty(data?.[sec.id]?.[f.key])).length;
  const totalFilled = SECTIONS.reduce((n, s) => n + filledCount(s), 0);
  const totalFields = SECTIONS.reduce((n, s) => n + s.fields.length, 0);
  const section = SECTIONS.find((s) => s.id === active);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadFile("/organizer/pdf", "business-aligned-organizer.pdf");
      toast.success("Your full blueprint downloaded as a PDF. Come back and edit anytime.");
    } catch {
      toast.error("Couldn't build the PDF — try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  if (!ready) return <div className="min-h-screen bg-[#FAF6EF]" />;

  if (!user?.paid) {
    return (
      <>
        <OrganizerSales user={user} onLogin={() => setAuthOpen(true)} />
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      </>
    );
  }

  if (!data) return <div className="min-h-screen bg-[#FAF6EF] flex items-center justify-center f-mono text-xs text-[#8C7562]">LOADING YOUR ORGANIZER…</div>;

  return (
    <main className="min-h-screen vector-grid pt-28 pb-24" data-testid="organizer-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-5 mb-10">
          <div>
            <span className="eyebrow">@{user.username}'s workspace</span>
            <h1 className="f-display text-3xl sm:text-4xl font-medium tracking-tight mt-3">Your Business Organizer</h1>
            <div className="mt-4">
              <IdeaSwitcher ideas={ideas} active={ideaId}
                onSwitch={(id) => { if (id !== ideaId) loadIdea(id); }}
                onCreated={(idea) => { setIdeas((l) => [...l, idea]); loadIdea(idea.idea_id); }}
                onRenamed={(id, name) => setIdeas((l) => l.map((i) => (i.idea_id === id ? { ...i, name } : i)))}
                onDeleted={(id) => { const rest = ideas.filter((i) => i.idea_id !== id); setIdeas(rest); if (id === ideaId) loadIdea(rest[0]?.idea_id || null); }} />
            </div>
            <p className="f-mono text-[11px] text-[#8C7562] mt-3" data-testid="save-status">
              {saveState === "saving" ? "SAVING…" : `ALL CHANGES SAVED · ${totalFilled}/${totalFields} FIELDS COMPLETE`}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button className="btn-ghost !py-2.5 !px-5 !text-xs" onClick={() => setAiOpen(true)} data-testid="open-ai-btn">
              <Sparkles size={15} className="text-[#C48A9F]" /> AI Copilot
            </button>
            <button className="btn-ghost !py-2.5 !px-5 !text-xs" onClick={downloadBrandKit} disabled={kitBusy} data-testid="download-brand-kit-btn">
              <Palette size={15} className="text-[#A8785A]" /> {kitBusy ? "Building…" : "Brand Kit"}
            </button>
            <button className="btn-primary !py-2.5 !px-5 !text-xs" onClick={downloadPdf} disabled={pdfBusy} data-testid="download-pdf-btn">
              <FileDown size={15} /> {pdfBusy ? "Building…" : "Download Full PDF"}
            </button>
          </div>
        </div>

        <div className="h-1.5 rounded-full bg-[#F3EAE0] mb-12 overflow-hidden" data-testid="progress-bar">
          <motion.div className="h-full bg-gradient-to-r from-[#D4A0B4] to-[#A8785A] rounded-full"
            animate={{ width: `${(totalFilled / totalFields) * 100}%` }} transition={{ duration: 0.6 }} />
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          <aside className="lg:sticky lg:top-28 self-start flex lg:flex-col gap-2 overflow-x-auto pb-2" data-testid="section-nav">
            {SECTIONS.map((s, i) => {
              const filled = filledCount(s);
              const complete = filled === s.fields.length;
              return (
                <button key={s.id} onClick={() => setActive(s.id)} data-testid={`section-tab-${s.id}`}
                  className={`text-left rounded-xl border px-4 py-3.5 min-w-[220px] lg:min-w-0 transition-colors ${
                    active === s.id ? "border-[#A8785A] bg-[#FFF3F8]" : "border-[#E8DCCB] bg-[#FFFDF7] hover:border-[#D4A0B4]"
                  }`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="f-mono text-[10px] text-[#A8785A]">{String(i + 1).padStart(2, "0")}</span>
                    {complete
                      ? <Check size={13} className="text-[#C48A9F]" />
                      : <span className="f-mono text-[10px] text-[#B9A896]">{filled}/{s.fields.length}</span>}
                  </div>
                  <p className="text-sm font-bold mt-1.5 leading-snug">{s.title}</p>
                </button>
              );
            })}
          </aside>

          <motion.section key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="glass rounded-2xl p-7 sm:p-10" data-testid={`section-panel-${active}`}>
            <span className="eyebrow">Section {String(SECTIONS.findIndex((s) => s.id === active) + 1).padStart(2, "0")}</span>
            <h2 className="f-display text-2xl sm:text-3xl font-medium tracking-tight mt-3">{section.title}</h2>
            <p className="text-sm text-[#8C7562] mt-2 mb-6">{section.blurb}</p>

            {section.links && (
              <div className="flex flex-wrap gap-2 mb-8">
                {section.links.map((l) => (
                  <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" data-testid="quick-link"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold border border-[#E8DCCB] bg-[#FFFDF7] rounded-full px-3.5 py-1.5 text-[#8C7562] hover:border-[#A8785A] hover:text-[#A8785A] transition-colors">
                    {l.label} <ExternalLink size={11} />
                  </a>
                ))}
              </div>
            )}

            <div className={section.id === "notes" ? "" : "grid sm:grid-cols-2 gap-5"}>
              {section.id === "branding" && <LogoUpload logo={logo} onChange={setLogo} ideaId={ideaId} />}
              {section.fields.map((f) => (
                <div key={f.key} className={f.long || f.doc ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">{f.label}</label>
                  <RichField key={`${ideaId}-${f.key}`} mode={f.doc ? "doc" : f.long ? "multi" : "single"} placeholder={f.placeholder}
                    value={data[section.id][f.key]} onChange={(v) => update(section.id, f.key, v)}
                    testid={`field-${section.id}-${f.key}`} />
                  {f.swatch && <Swatches value={data[section.id][f.key]} />}
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-10 pt-6 border-t border-[#E8DCCB]">
              <button className="btn-ghost !py-2 !px-4 !text-xs" data-testid="prev-section-btn"
                disabled={SECTIONS[0].id === active}
                onClick={() => setActive(SECTIONS[SECTIONS.findIndex((s) => s.id === active) - 1].id)}>
                ← Previous
              </button>
              <button className="btn-ghost !py-2 !px-4 !text-xs" data-testid="next-section-btn"
                disabled={SECTIONS[SECTIONS.length - 1].id === active}
                onClick={() => setActive(SECTIONS[SECTIONS.findIndex((s) => s.id === active) + 1].id)}>
                Next section →
              </button>
            </div>
          </motion.section>
        </div>
      </div>
      <AIAssistant key={aiKey} ideaId={ideaId} open={aiOpen} onClose={() => setAiOpen(false)} onApply={applyFromCopilot} brandVoice={richToText(data.branding.brand_voice)} />
    </main>
  );
}
